<template>
  <div class="border-top p-2 d-flex align-items-end rs-bg-content">
    <div class="mr-2" :style="{ width: '100px' }">
      <label>Link Type</label>
      <select v-model="linkType" class="custom-select custom-select-sm">
        <option v-for="option in linkTypes" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>
    </div>

    <div class="mr-2 flex-grow-1">
      <label>{{ linkTypes.find((l) => l.value === linkType)?.text }}</label>
      <div class="input-group input-group-sm">
        <div v-if="!['Email', 'Phone'].includes(linkType)" class="input-group-prepend">
          <button
            type="button"
            class="btn btn-secondary"
            title="Upload and link to file"
            @click="handleUploadAndLink"
          >
            <i class="far fa-file-plus" />
          </button>
        </div>

        <input
          class="form-control"
          :value="hrefInput"
          @input="handleHrefInput(($event.target as HTMLInputElement).value)"
        />

        <div v-if="!['Email', 'Phone'].includes(linkType)" class="input-group-append">
          <a
            v-if="href"
            :href="href"
            target="_blank"
            class="btn btn-outline-secondary"
            title="Open link in new tab"
          >
            <i class="far fa-external-link" />
          </a>
        </div>
      </div>
    </div>

    <div :style="{ width: '135px' }">
      <label>Target</label>
      <select
        :value="target"
        class="custom-select custom-select-sm"
        @change="handleChange('target', ($event.target as HTMLSelectElement).value)"
      >
        <option v-for="option in targetTypes" :key="option.value" :value="option.value">
          {{ option.text }}
        </option>
      </select>
    </div>

    <div class="flex-grow-0 align-self-end ml-2">
      <button
        type="button"
        class="btn btn-light btn-sm"
        title="Remove Link"
        @click="editor.chain().focus().unsetLink().run()"
      >
        <i class="far fa-trash-alt rs-text-error" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { Editor } from '@tiptap/vue-3';
import { linkTypes, targetTypes } from '@/shared/tiptap/consts';
import { uploadWithFileDialog } from '@/shared/tiptap/extensions/file-handler';
import { TEditableLink } from '@/shared/tiptap/types';

/**
 * Various methods for formatting the `href` attribute of the anchor tag we are making.
 */
export const formatHref: Record<string, (href: string) => string> = {
  URL: (href: string) => {
    if (!href.startsWith('http')) {
      return `https://${href}`
        .replace('mailto:', '')
        .replace('tel:+1', '');
    }

    return href;
  },

  Email: (href: string) => `mailto:${href}`
    .replace('https://', '')
    .replace('http://', '')
    .replace('tel:+1', ''),

  Phone: (href: string) => {
    const phoneFormatted = href.replace(/\D/g, '');

    return phoneFormatted.startsWith('1') ? `tel:+${phoneFormatted}` : `tel:+1${phoneFormatted}`;
  },
};

/**
 * Returns the link type based on `href` format.
 * @param href
 */
export const getHrefType = (href: string) => {
  if (href.startsWith('tel:+')) {
    return 'Phone';
  }

  if (href.startsWith('mailto:')) {
    return 'Email';
  }

  return 'URL';
};

/**
 * Formats the `href` for editing by removing its modifiers.
 * @param href
 */
export const unformatHref = (href: string) => {
  if (href.startsWith('tel:+')) {
    return href.replace('tel:+', '');
  }

  if (href.startsWith('mailto:')) {
    return href.replace('mailto:', '');
  }

  return href;
};

export default defineComponent({
  props: {
    /**
     * Editor instance shared by toolbar and form parent components.
     */
    editor: {
      type: Object as PropType<Editor>,
      required: true,
    },

    /**
     * Callback method for when a file upload has started on the link editor.
     */
    onFileUploadStart: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },

    /**
     * Callback method for when a file upload has succeeded on the link editor.
     */
    onFileUploadSuccess: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },

    /**
     * Callback method for when a file upload has failed on the link editor.
     */
    onFileUploadError: {
      type: Function as PropType<() => void>,
      default: () => () => {},
    },
  },

  data() {
    return {
      formatHref,
      linkTypes,
      targetTypes,
      linkType: 'URL',
      href: '',
      hrefInput: '',
      target: '_blank',
    };
  },

  watch: {
    linkType(newVal: string) {
      this.handleChange('href', this.formatHref[newVal](this.href));
    },

    'editor.state.selection': {
      handler() {
        if (this.editor.isFocused && this.editor.isActive('link')) {
          this.setInitialState();
        }
      },
    },
  },

  mounted() {
    this.setInitialState();
  },

  methods: {
    unformatHref,

    /**
     * Sets initial state for link's attributes.
     */
    setInitialState() {
      const link = this.editor.getAttributes('link');
      this.href = unformatHref(link.href ?? '');
      this.hrefInput = this.href;
      this.linkType = getHrefType(link.href ?? '');
      this.target = link.target ?? '_blank';
    },

    /**
     * Holds the raw text the user typed so the URL field stays a faithful mirror of the keyboard.
     *
     * `handleChange` only writes the *formatted* href into the editor; it never feeds a value back
     * to this input. Binding the input straight to `href` therefore left it uncontrolled: `href`
     * stayed at the last committed link while the DOM held the new text, and the next re-render
     * (every editor transaction triggers one) patched `input.value` back to the stale href, which
     * also snaps the caret to the end mid-word. `BFormInput` used to absorb this with its own
     * `localValue` buffer; `hrefInput` is that buffer.
     */
    handleHrefInput(value: string) {
      this.hrefInput = value;
      this.handleChange('href', this.formatHref[this.linkType](value));
    },

    /**
     * When attribute `key`'s `value` has changed, update it on the editor and emit it via `@input`
     * @param key
     * @param content
     */
    handleChange(key: keyof TEditableLink, content: string) {
      const link = this.editor.getAttributes('link') as TEditableLink;

      if (!link) {
        return;
      }

      const newValue = { ...link, [key]: content };

      this.editor
        .chain()
        .extendMarkRange('link')
        .setLink(newValue)
        .run();
    },

    /**
     * Handles a file upload for the link editor, then sets the link's `href` attribute
     * to the uploaded file's S3 location.
     */
    async handleUploadAndLink() {
      await uploadWithFileDialog({
        onUploadStart: this.onFileUploadStart,
        onUploadSuccess: (filename) => {
          this.href = filename;
          this.handleChange('href', filename);
          this.onFileUploadSuccess();
        },
        onUploadError: this.onFileUploadError,
        fileRestrict: 'none',
      });
    },
  },
});
</script>

<style lang="scss">
@import "@/styles/_variables.scss";
</style>
